function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }
  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    document.body.classList.remove('overflow-hidden-tablet');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ToggleElementClass extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const selectedOptionName = this.querySelector('.selected-option-wrapper.matboard .selected-option-title');
    const selectedAvilable = this.querySelector('.options-available');
    if( selectedOptionName && selectedAvilable) {
      selectedOptionName.classList.add('hidden');
      selectedAvilable.classList.remove('hidden');
    }
    this.addEventListener('click', () => {
      const wrapper = this.closest('.drawer-wrapper');
      const drawer = wrapper.querySelector('my-drawer');
      const overlay = wrapper.querySelector('.drawer-overlay');

      const isOpen = drawer.classList.contains('active');

      if (isOpen) {
        document.body.classList.remove('overflow-hidden');
        drawer?.classList.remove('active');
        overlay?.classList.remove('active');
      } else { 
        document.body.classList.add('overflow-hidden');
        drawer?.classList.add('active');
        overlay?.classList.add('active');
      }
    });
  }
}

customElements.define('toggle-element-class', ToggleElementClass);

class InformationToggleElementClass extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const closeButton = this.querySelector('.drawer-close-button');
    const drawer = this.querySelector('information-draggable-drawer');
    const overlay = this.querySelector('.information-drawer-overlay');

    const closeDrawer = () => {
      document.body.classList.remove('overflow-hidden');
      this.classList.remove('active');
      drawer?.classList.remove('active');
      overlay?.classList.remove('active');

      // Also close all other drawers and overlays
      const dragable_drawers = document.querySelectorAll("my-drawer");
      const dragable_overlays = document.querySelectorAll(".drawer-overlay");

      dragable_drawers.forEach(drawer => {
        drawer.classList.remove('active');
      });

      dragable_overlays.forEach(overlay => {
        overlay.classList.remove('active');
      });
    };

    if (closeButton) {
      closeButton.addEventListener('click', (e) => {
        e.stopPropagation(); // Prevent toggle from firing
        closeDrawer();
      });
    }

    this.addEventListener('click', (e) => {
      e.stopPropagation();
      const dragable_drawers = document.querySelectorAll("my-drawer");
      const dragable_overlays = document.querySelectorAll(".drawer-overlay");
      initSlider('.basicSwiper', '.basicThumbs', '.basic-next', '.basic-prev');
      initSlider('.classicSwiper', '.classicThumbs', '.classic-next', '.classic-prev');
      initSlider('.coloredSwiper', '.coloredThumbs', '.colored-next', '.colored-prev');
      document.body.classList.add('overflow-hidden');
      drawer?.classList.add('active');
      overlay?.classList.add('active');
      this.classList.add('active');
    });

    overlay.addEventListener('click', (e) => {
      setTimeout(() => {
        closeDrawer();
      }, 300);
    });
  }
}

customElements.define('information-drawer', InformationToggleElementClass);
function initSlider(mainClass, thumbsClass, nextClass, prevClass) {
  const mainEl = document.querySelector(mainClass);
  const thumbsEl = document.querySelector(thumbsClass);

  if (mainEl?.swiper || thumbsEl?.swiper) return;

  const thumbs = new Swiper(thumbsClass, {
    slidesPerView: 5,
    spaceBetween: 10,
    loop: true,
    watchSlidesProgress: true,
  });

  new Swiper(mainClass, {
    spaceBetween: 10,
    loop: true,
     navigation: {
      nextEl: nextClass,
      prevEl: prevClass,
    },
    thumbs: {
      swiper: thumbs,
    },
  });
}


    // initSlider('.basicSwiper', '.basicThumbs', '.basic-next', '.basic-prev');
    // initSlider('.classicSwiper', '.classicThumbs', '.classic-next', '.classic-prev');
    // initSlider('.coloredSwiper', '.coloredThumbs', '.colored-next', '.colored-prev');


class InformationDraggableDrawer extends HTMLElement {
  constructor() {
    super();
  }
  
}


customElements.define('information-draggable-drawer', InformationDraggableDrawer);



class DraggableDrawer extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const frameInputs = this.querySelectorAll('.product-form__input.Frame:not(.Size) input');
    const sizeInputs = this.querySelectorAll('.product-form__input.Size:not(.Frame) input');
    const matBoards = this.querySelectorAll('.product-form__input.matboard input');
    const frameImage = document.querySelector('.product-media-slider__frame');
    const selectedFrame = this.querySelector('.product-form__input.Frame input:checked[data-frame-image]');
    const selectedSize = this.querySelector('.product-form__input.Size:not(.Artwork):not(.Frame) input:checked')?.value;
    const selectedMatboard = this.querySelector('.product-form__input.matboard input:checked');
    const closeButton = this.querySelector('.drawer-close-button');
    const matBoardDrawer = document?.querySelector('.drawer-wrapper[data-drawer-id="matboard"]');
    const getMatboardRadios = () => document.querySelectorAll('.drawer-wrapper[data-drawer-id="matboard"] input[type="radio"]');
    const selectedTitleElement = matBoardDrawer?.querySelector('.selected-option-title');
    const selectedPriceElement = matBoardDrawer?.querySelector('.selected-option-prices .selected-option-price');
    const selectedComparePriceElement = matBoardDrawer?.querySelector('.selected-option-prices .compare-price');
    const formIdinput = document?.querySelector("input[name='id2']");
    const formIdFrameinput = document?.querySelector("input[name='id3']");
    const productMatboardImage = document?.querySelector(".product-media-slider__matboard");
    const typeInput = document.querySelectorAll('.product-form__input.Type input');
    const selectedtypeInput = this.querySelector('.product-form__input.Type input:checked');
    const messurementInput = document.querySelectorAll('.product-form__input .measurement-block .measurement');
    const frameSizeInput = this.querySelectorAll('.product-form__input.Frame.Size input');
    const fvarTitle = this.querySelector('.frame.title-price-wrap .var-title');
    const fvarPrice = this.querySelector('.frame.title-price-wrap .var-price');
    const mvarTitle = this.querySelector('.matboard.title-price-wrap .var-title');
    const mvarPrice = this.querySelector('.matboard.title-price-wrap .var-price');

    const updateFrameSize = (input) => {
      setTimeout(() => {
        const artworkSize = document.querySelectorAll('.product-form__input.Artwork input');     
        const checkedInput = Array.from(artworkSize).find((input) => input.checked);

        if (checkedInput && checkedInput.classList.contains("disabled")) {
          // uncheck the disabled one
          checkedInput.checked = false;

          // find the first available (not disabled)
          const firstAvailable = Array.from(artworkSize).find(
            (input) => !input.classList.contains("disabled")
          );

          if (firstAvailable) {
            firstAvailable.checked = true;
            // trigger Shopify PDP update
            firstAvailable.dispatchEvent(new Event("change", { bubbles: true }));
          }
        }
      }, 1000);
      closeDrawer();
    }
    
    const updateFrameImage = (src,input) => {
      if (!frameImage) return;
      
      const currentTypeInput = document.querySelector('.product-form__input.Type input:checked');      
      const currentMatboardInput = document.querySelector('.product-form__input.matboard input:checked');
      const currentTypeInputValue = currentTypeInput?.value;      
      const isCanvas = currentTypeInputValue?.trim() === "Canvas";
      const optionTitle = document.querySelector('.selected-option-wrapper.Frame .selected-option-title');
      const optionPrice = document.querySelector('.selected-option-wrapper.Frame .selected-option-prices .selected-option-price');
      const optionComparePrice = document.querySelector('.selected-option-wrapper.Frame .selected-option-prices .compare-price');
      const optionsAvailable = document.querySelector('.selected-option-wrapper.Frame .options-available');
      const pdpMediaSlide = document.querySelector('.product-media-slider__frame-wrapper');
      const borderimage = document.querySelector('.product-media-slider__borderImage');
      const variantId = input?.dataset?.variantId || '';
      const newTitle = input?.value || '';
      const newPrice = input?.dataset?.framePrice || '';
      const newComparePrice = input?.dataset?.frameComparePrice || '';

      const frameSku = input?.dataset?.frameSku || '';

      const computed = window.getComputedStyle(frameImage);

      if (!frameImage.src || computed.opacity === "0") {
        getMatboardRadios().forEach(radio => {
          radio.disabled = true;
          radio.checked = false;
        });
      }

      const numericPrice = parseFloat(newPrice.replace(/[^0-9.]/g, '')) || 0; 
      const numericcomparePrice = parseFloat(newComparePrice.replace(/[^0-9.]/g, '')) || 0; 
      
      optionTitle.textContent = newTitle;
      optionPrice.textContent = newPrice;
      if(newPrice != '') {
        optionPrice.classList.add('showPrice');
      } else {
        optionPrice.classList.remove('showPrice');
      }
      optionComparePrice.textContent = newComparePrice;

      if(input) {
        optionsAvailable.classList.add('hidden'); 
        optionTitle.classList.remove('hidden');
      }
      if(variantId) {
        formIdFrameinput.value = variantId;
        formIdFrameinput.setAttribute('data-frame-title', input.value.trim());
        formIdFrameinput.setAttribute('data-frame-price', numericPrice);
        formIdFrameinput.setAttribute('data-frame-compare-price', numericcomparePrice);
        formIdFrameinput.setAttribute('data-frame-sku', frameSku);
        fvarTitle.textContent = input.value.trim();
        fvarPrice.textContent = '+$' + numericPrice;
      } else {
        formIdFrameinput.value = "";
        formIdFrameinput.setAttribute('data-frame-title', '');
        formIdFrameinput.setAttribute('data-frame-price', 0);
        formIdFrameinput.setAttribute('data-frame-compare-price', '');
        formIdFrameinput.setAttribute('data-frame-sku', '');
        fvarTitle.textContent = input.value.trim();
        fvarPrice.textContent = '';
      }
      if (src) {        
        frameImage.src = src;
        borderimage.style.opacity = 0;
        frameImage.style.opacity = 1;
        if (!isCanvas) {
          const varinantImage = currentMatboardInput?.dataset?.matboardImage || '';
          matBoardDrawer?.classList.remove('hidden');
          getMatboardRadios().forEach(radio => {
            radio.disabled = false;
          });
          const variantId = currentMatboardInput?.dataset?.variantId || '';
          const variantPrice = currentMatboardInput?.dataset?.matboardPrice || '';
          const numericPrice = parseFloat(variantPrice.replace(/[^0-9.]/g, '')) || 0;  
          const variantComparePrice = currentMatboardInput?.dataset?.matboardComparePrice || '';
          const numericcomparePrice = parseFloat(variantComparePrice.replace(/[^0-9.]/g, '')) || 0;  
          const matboardSku = currentMatboardInput?.dataset?.matboardSku || '';

          if (varinantImage) {
            productMatboardImage.style.opacity = 1;
            pdpMediaSlide.classList.add('matboradSize');
          }
          formIdinput.value = variantId;
          formIdinput.setAttribute('data-matboard-title', input.value.trim());
          formIdinput.setAttribute('data-matboard-price', numericPrice);
          formIdinput.setAttribute('data-matboard-compare-price', numericcomparePrice);
          formIdinput.setAttribute('data-matboard-sku', matboardSku);
        } else {          
          matBoardDrawer?.classList.add('hidden');
          getMatboardRadios().forEach(radio => {
            radio.disabled = true;
          });
          pdpMediaSlide.classList.remove('matboradSize');
          formIdinput.value = "";
          formIdinput.setAttribute('data-matboard-title', '');
          formIdinput.setAttribute('data-matboard-price', 0);
          formIdinput.setAttribute('data-matboard-compare-price', 0);
          formIdinput.setAttribute('data-matboard-sku', '');
          productMatboardImage.style.opacity = 0;
        }
      } else {
        borderimage.style.opacity = 1;
        frameImage.style.opacity = 0;
        matBoardDrawer?.classList.add('hidden');
        getMatboardRadios().forEach(radio => {
          radio.disabled = true;
        });
        pdpMediaSlide.classList.remove('matboradSize');
        formIdinput.value = "";
        formIdinput.setAttribute('data-matboard-title', '');
        formIdinput.setAttribute('data-matboard-price', 0);
        formIdinput.setAttribute('data-matboard-compare-price', 0);
        formIdinput.setAttribute('data-matboard-sku', '');
        productMatboardImage.style.opacity = 0; 
      }
      
      const imageEl = input?.nextElementSibling?.querySelector('img');
      const newoptionImage = imageEl?.src || '';
      const currentOptionImage = document?.querySelector('.selected-option-wrapper.Frame img.option-image');

      if (currentOptionImage) {        
        currentOptionImage.setAttribute('src', newoptionImage);
        currentOptionImage.setAttribute('data-src', newoptionImage);
      }
      updateDisplayedPrice();
      closeDrawer();
    };
    
    const updateSizeClass = (size) => {
      const currentTypeInput = document.querySelector('.product-form__input.Type input:checked');
      const currentTypeInputValue = currentTypeInput?.value;      
      const isCanvas = currentTypeInputValue?.trim() === "Canvas";
      
      getMatboardRadios().forEach(radio => {
        radio.disabled = true;
      });

      if (!frameImage) return;
      const container = frameImage.closest('li');
      if (!container) return;
      
      // Remove previously set inline style
      container.style.maxWidth = '';

      // Get index of selected size
      const sizes = Array.from(sizeInputs);
      const selectedIndex = sizes.findIndex(input => input.value === size);
      const isMobile = window.innerWidth <= 768;
      const baseWidth = isMobile ? 84 : 80;
      const increment = 2;

      if (selectedIndex !== -1) {
        const newWidth = baseWidth + selectedIndex * increment;
        container.style.maxWidth = `${newWidth}%`;
      }

      const selectedMatboardPrice = document.querySelector('.product-form__input.matboard input:checked')?.dataset.matboardPrice || '';     
      const selectedFramePrice = document.querySelector('.product-form__input.Frame input:checked')?.dataset.framePrice || '';
      
      if (selectedMatboardPrice || selectedFramePrice) { 
        setTimeout(() => {     
          const selectedFrameInputLength = document.querySelectorAll('.product-form__input.Frame input').length;
          if(selectedFrameInputLength < 2) {
            updateFrameImage();
          }       
          updateDisplayedPrice();
        }, 1000);
      }
      closeDrawer();
    };

    const updateMatBoard = (input) => {    
      
      const variantId = input?.dataset?.variantId || '';
      const variantImage = input?.dataset?.matboardImage || '';
      const variantPrice = input?.dataset?.matboardPrice || '';
      const numericPrice = parseFloat(variantPrice.replace(/[^0-9.]/g, '')) || 0;  
      const variantComparePrice = input?.dataset?.matboardComparePrice || '';
      const numericcomparePrice = parseFloat(variantComparePrice.replace(/[^0-9.]/g, '')) || 0;  
      const optionsAvailable = document.querySelector('.selected-option-wrapper.matboard .options-available');
      const selectedAvilable = document.querySelector('.selected-option-wrapper.matboard .selected-option-title');
      const pdpMediaSlide = document.querySelector('.product-media-slider__frame-wrapper');

      const imageEl = input?.nextElementSibling?.querySelector('img');
      const newoptionImage = imageEl?.src || '';
      const currentmatboardOptionImage = document?.querySelector('.selected-option-wrapper.matboard img.option-image');

      const matboardSku = input?.dataset?.matboardSku || '';

      if (selectedTitleElement && input && input.value) {
        optionsAvailable.classList.add('hidden');
        selectedAvilable.classList.remove('hidden');
        selectedTitleElement.textContent = input.value.trim();
        formIdinput.value = variantId;
        formIdinput.setAttribute('data-matboard-title', input.value.trim());
        formIdinput.setAttribute('data-matboard-price', numericPrice);
        formIdinput.setAttribute('data-matboard-compare-price', numericcomparePrice);
        formIdinput.setAttribute('data-matboard-sku', matboardSku);
        mvarTitle.textContent = input.value.trim();
        mvarPrice.textContent = variantPrice ? '+' + variantPrice : variantPrice;
        selectedPriceElement.textContent = variantPrice;
        if(variantPrice != '') {
          selectedPriceElement.classList.add('showPrice');
        } else {
          selectedPriceElement.classList.remove('showPrice');
        }
        selectedComparePriceElement.textContent = variantComparePrice;
        if (currentmatboardOptionImage) {        
          currentmatboardOptionImage.setAttribute('src', newoptionImage);
          currentmatboardOptionImage.setAttribute('data-src', newoptionImage);
        }
      }
      
      if (variantImage) {
        productMatboardImage.src = variantImage;
        productMatboardImage.style.opacity = 1;     
        pdpMediaSlide.classList.add('matboradSize');
      } else {
        productMatboardImage.style.opacity = 0;      
        pdpMediaSlide.classList.remove('matboradSize');
      }
      updateDisplayedPrice();
      closeDrawer();
    };

    const updateDisplayedPrice = () => {
      const comparePriceElement = document.querySelector('.product__info-wrapper .price__sale .price-item.price-item--regular');
      let priceElement;
      const totalCartPrice = document.querySelector('.product-form__buttons .totalCartPrice');
      
      if (comparePriceElement.dataset.comparePrice.trim() === '') { 
        priceElement = document.querySelector('.product__info-wrapper .price__container .price-item.price-item--regular #money-price');
        console.log(priceElement && priceElement.dataset.mainPrice, priceElement && priceElement.textContent);
      } else {
        priceElement = document.querySelector('.product__info-wrapper .price__container .price-item.price-item--sale');
      }

      const mainPrice = priceElement?.dataset?.mainPrice || '0';
      const mainComparePrice = comparePriceElement?.dataset?.comparePrice || '0';
      const currencyCode = priceElement?.dataset?.currency || 'USD';

      const basePrice = parseFloat(mainPrice.replace(/[^0-9.-]+/g, "")) || 0;
      const baseComparePrice = parseFloat(mainComparePrice.replace(/[^0-9.-]+/g, "")) || 0;

      const framePrice = parseFloat(formIdFrameinput?.getAttribute('data-frame-price') || 0);
      const matboardPrice = parseFloat(formIdinput?.getAttribute('data-matboard-price') || 0);
      
      const frameComparePrice = parseFloat(formIdFrameinput?.getAttribute('data-frame-compare-price') || 0);
      const matboardComparePrice = parseFloat(formIdinput?.getAttribute('data-matboard-compare-price') || 0);

      const totalPrice = basePrice + framePrice + matboardPrice;
      const totalComparePrice = baseComparePrice + frameComparePrice + matboardComparePrice;

      priceElement.textContent = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      }).format(totalPrice);

      totalCartPrice.textContent = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      }).format(totalPrice);

      comparePriceElement.textContent = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
      }).format(totalComparePrice);
    };

    const updateType = (input) => {
      const isCanvas = input.value.trim() === "Canvas";
      const checkedInput = document.querySelector('.product-form__input.Frame input:checked');
      const checkFrame = checkedInput ? checkedInput.getAttribute('data-frame-image') : '';
      const matboardTitle = document?.querySelector('.drawer-wrapper[data-drawer-id="matboard"] .selected-option-title');
      const matboardAvailable = document.querySelector('.drawer-wrapper[data-drawer-id="matboard"] .options-available');
      const pdpMediaSlide = document.querySelector('.product-media-slider__frame-wrapper');

      if(checkedInput) {
        checkedInput.checked = false;
        frameImage.style.opacity = 0;
      }      
      
      if (isCanvas) {
        matBoardDrawer?.classList.add('hidden'); 
        getMatboardRadios().forEach(radio => {
          radio.disabled = true;
        });
        pdpMediaSlide.classList.remove('matboradSize');
        formIdinput.value = "";
        formIdinput.setAttribute('data-matboard-title', '');
        formIdinput.setAttribute('data-matboard-price', 0);
        formIdinput.setAttribute('data-matboard-compare-price', 0);
        formIdinput.setAttribute('data-matboard-sku', '');
        if (productMatboardImage) productMatboardImage.style.opacity = 0;
      } else {        
        matBoardDrawer?.classList.remove('hidden');
        getMatboardRadios().forEach(radio => {
          radio.disabled = false;
        });
        document?.querySelectorAll('.drawer-wrapper[data-drawer-id="matboard"] input').forEach((matboardInput) => {
          matboardInput.checked = false;
        });
        matboardTitle.classList.add('hidden');
        matboardAvailable.classList.remove('hidden');
        selectedComparePriceElement.textContent = '';
        selectedPriceElement.textContent = '';
        selectedPriceElement.classList.remove('showPrice');
        const currentmatboardOptionImage = document?.querySelector('.selected-option-wrapper.matboard img.option-image');
        currentmatboardOptionImage.setAttribute('src', '');
        currentmatboardOptionImage.setAttribute('data-src', '');
      }

      const selectedMatboardPrice = document.querySelector('.product-form__input.matboard input:checked')?.dataset.matboardPrice || '';     
      const selectedFramePrice = document.querySelector('.product-form__input.Frame input:checked')?.dataset.framePrice || '';
      
      if (selectedMatboardPrice || selectedFramePrice) { 
        setTimeout(() => {              
          updateDisplayedPrice();
        }, 1000);
      }
      closeDrawer();
    };

    const closeDrawer = () => {
      document.body.classList.remove('overflow-hidden');
      this.classList.remove('active');
      // const overlay = this.closest('.drawer-wrapper').querySelector('.drawer-overlay');
      overlay.classList.remove('active');
    }

    getMatboardRadios().forEach(radio => {
      radio.disabled = true;
      radio.checked = false;
    });

    // Initial setup
    if (selectedFrame) updateFrameImage(selectedFrame.getAttribute('data-frame-image'),selectedFrame);
    if (selectedSize) updateSizeClass(selectedSize);

    sizeInputs.forEach((input) => {
      input.addEventListener('change', () => {
        document.body.classList.remove('overflow-hidden');
        updateSizeClass(input.value);
      });
    });

    frameInputs.forEach((input) => {
      input.addEventListener('change', () => {
        setTimeout(() => {
          updateDisplayedPrice();
        }, 1000);
        
        document.body.classList.remove('overflow-hidden');
        updateFrameImage(input.getAttribute('data-frame-image'),input);
        fvarTitle.textContent = input.value.trim();
        fvarPrice.textContent = '+$' + numericPrice;
        $('.product_slider_slick').slick('slickGoTo', 0);
      });
    });

    matBoards.forEach((input) => {
      input.addEventListener('change', () => {
        document.body.classList.remove('overflow-hidden');
        updateMatBoard(input);
        mvarTitle.textContent = input.value.trim();
        mvarPrice.textContent = '+$' + numericPrice;
        $('.product_slider_slick').slick('slickGoTo', 0);
      })
    })

    typeInput.forEach((input) => {
      input.addEventListener('change', () => {
        document.body.classList.remove('overflow-hidden');
        updateType(input);
      })
    })

    document.addEventListener('change', function (e) {
      if (e.target.matches('.product-form__input.Type input')) {
        console.log("TYPE CHANGED:", e.target.value, e.target);
        updateType(e.target);
      }
    });

    frameSizeInput.forEach((input) => {
      input.addEventListener('change', () => {
        document.body.classList.remove('overflow-hidden');
        updateFrameSize(input);
      })
    })

    messurementInput.forEach((input) => {
      input.addEventListener('click', () => {

        const parentSize = input.closest('.product-form__input.Size');
        const inputMeasurement = input.dataset.measurement;

        // update data attribute only on this size block
        parentSize.setAttribute('data-measurement', inputMeasurement);

        // remove active only within this size block
        parentSize.querySelectorAll('[data-measurement]').forEach(btn => 
          btn.classList.remove('active')
        );

        // add active to the clicked one
        input.classList.add('active');
      });
    });

    // collection Frame script

    const collectionFramebuttons = document.querySelectorAll('.collection-frame-category .frame-button');
    const collectionProducts = document.querySelectorAll('.product-grid-container .card-wrapper');
    const frameButtons = document.querySelectorAll('.button-frame');
    const matchArtworkInput = this.querySelector('#drawer-match-artwork');

    const STORAGE_KEY_LABEL = 'selectedFrameLabel';
    const STORAGE_KEY_CATEGORY = 'selectedFrameCategory';

    function getCollectionProducts() {
      return document.querySelectorAll('.product-grid-container .card-wrapper');
    }
    
    matchArtworkInput?.addEventListener('change', () => {
      const isChecked = matchArtworkInput.checked;
      if (isChecked) {
        collectionFramebuttons.forEach((btn) => {
          btn.classList.remove('active');
        });
        collectionProducts.forEach((product) => {
          const frameShape = product.dataset.frameShape.trim();
          const matchArtwork = JSON.parse(product.dataset.matchArtwork);
          const frameImage = matchArtwork[frameShape];
          
          const productFrameImage = product.querySelector('.product-media__frame');

          if (productFrameImage) {
            if (frameImage) {
              productFrameImage.src = frameImage;
              productFrameImage.classList.add('active');
            } else {
              productFrameImage.src = '';
              productFrameImage.classList.remove('active');
            }
          }
        })
      } else {
        const savedLabel = sessionStorage.getItem(STORAGE_KEY_LABEL);
        const savedCategory = sessionStorage.getItem(STORAGE_KEY_CATEGORY);
        if(savedLabel && savedCategory) {
          activateFrameButtonByLabel(savedLabel, savedCategory);
        } else {
          collectionProducts.forEach((product) => {
            const productFrameImage = product.querySelector('.product-media__frame');
            productFrameImage.src = '';
            productFrameImage.classList.remove('active');
          })
        }
      }
    })
    
    function updateProductFrames(frameImageOverlay) {
      getCollectionProducts().forEach((product) => {
        const frameShape = product.dataset.frameShape.trim();
        const frameImage = frameImageOverlay[frameShape];
        const productFrameImage = product.querySelector('.product-media__frame');

        if (productFrameImage) {
          if (frameImage) {
            productFrameImage.src = frameImage;
            productFrameImage.classList.add('active');
          } else {
            productFrameImage.src = '';
            productFrameImage.classList.remove('active');
          }
        }
      });
    }

    function activateFrameButtonByLabel(label,category) {
      collectionFramebuttons.forEach((btn) => {
        const itemFrameLabel = btn.dataset.frameLabel;
        const itemFrameCategory = btn.dataset.frameCategory;
        if (itemFrameLabel === label && itemFrameCategory === category) {
          btn.classList.add('active');
          const frameImageOverlay = JSON.parse(btn.dataset.frameImageOverlays);
          updateProductFrames(frameImageOverlay);
        } else {
          btn.classList.remove('active');
        }
      });

      frameButtons.forEach((btn) => {
        if (btn.dataset.selectFrame === label && btn.dataset.selectFrameCategory === category) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });
    }

    frameButtons.forEach((button) => {
      button.addEventListener('click', () => {
        frameButtons.forEach((btn) => btn.classList.remove('active'));
        button.classList.add('active');
        const buttonSelectFrameLabel = button.dataset.selectFrame;
        const buttonSelectFramecategory = button.dataset.selectFrameCategory;
        sessionStorage.setItem(STORAGE_KEY_LABEL, buttonSelectFrameLabel);
        sessionStorage.setItem(STORAGE_KEY_CATEGORY, buttonSelectFramecategory);
        activateFrameButtonByLabel(buttonSelectFrameLabel,buttonSelectFramecategory);
      });
    });

    collectionFramebuttons.forEach((button) => {
      button.addEventListener('click', () => {
        collectionFramebuttons.forEach((btn) => btn.classList.remove('active'));
        button.classList.add('active');
        const label = button.dataset.frameLabel;
        const category = button.dataset.frameCategory;
        sessionStorage.setItem(STORAGE_KEY_LABEL, label);
        sessionStorage.setItem(STORAGE_KEY_CATEGORY, category);
        activateFrameButtonByLabel(label,category);
      });
    });

    const savedLabel = sessionStorage.getItem(STORAGE_KEY_LABEL);
    const savedCategory = sessionStorage.getItem(STORAGE_KEY_CATEGORY);

    if (savedLabel && savedCategory) {
      activateFrameButtonByLabel(savedLabel, savedCategory);
    }

    closeButton.addEventListener('click', () => {
      closeDrawer();
    });
    window.activateFrameButtonByLabel = activateFrameButtonByLabel;
    window.matchArtworkInput = this.querySelector('#drawer-match-artwork');
  }
}

customElements.define('my-drawer', DraggableDrawer);

class CardOverview extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const buttons = this.querySelectorAll('.card-overview__button');
    const STORAGE_KEY = 'productOverviewView';
    const container = document.querySelector('.collection-inner');
    const searchwrap = document.querySelector('.template-search')

    const savedView = sessionStorage.getItem(STORAGE_KEY);

    if (buttons.length > 0) {
      let matched = false;
      buttons.forEach((button) => {
        if (button.dataset.view === savedView) {
          container?.setAttribute('grid-view', button.dataset.view);
          searchwrap?.setAttribute('grid-view', button.dataset.view);
          button.classList.add('active');
          matched = true;
        } else {
          button.classList.remove('active');
        }
      });

      if (!matched) {
        buttons[0].classList.add('active');
        sessionStorage.setItem(STORAGE_KEY, buttons[0].dataset.view);
        container?.setAttribute('grid-view', buttons[0].dataset.view);
        searchwrap?.setAttribute('grid-view', buttons[0].dataset.view);
      }
    }

    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        buttons.forEach((btn) => btn.classList.remove('active'));
        button.classList.add('active');
        const selectedView = button.dataset.view;
        sessionStorage.setItem(STORAGE_KEY, selectedView);
        container?.setAttribute('grid-view', selectedView);
        searchwrap?.setAttribute('grid-view', selectedView);
      });
    });

  }
}

customElements.define('card-overview', CardOverview);

// Handle overlay click for all drawers

class DrawerOverlay extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('click', () => {
      const wrapper = this.closest('.drawer-wrapper');
      const drawer = wrapper.querySelector('my-drawer');
      drawer.classList.remove('active');
      this.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
    })
  }
}

customElements.define('drawer-overlay', DrawerOverlay);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }
  Related_Swiper () {
      const swiper = new Swiper(".related-products-swiper ", {
      slidesPerView: 1,
      spaceBetween: 24,
      navigation: {
        nextEl: ".related-products .arrow-right",
        prevEl: ".related-products .arrow-left",
      },
      breakpoints: {
        768: {
          slidesPerView: 2
        },
        1024: {
          slidesPerView: 3
        },
        1250: {
          slidesPerView: 4
        },
      },
    });
      }
  
  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');
        
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.Related_Swiper()
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

 

    var swiper = new Swiper(".collection-list-swiper", {
    slidesPerView: "auto",
    centeredSlides: false,
    navigation: {
        nextEl: ".swiper-bttn-next",
        prevEl: ".swiper-bttn-prev",
      },
  });

  var collectionsNavSwiper = document.querySelector('.swiper.collections-nav__swiper');
  if (collectionsNavSwiper) {
    new Swiper(collectionsNavSwiper, {
      slidesPerView: 'auto',
      spaceBetween: 0, 
      navigation: {
        nextEl: '.collection-bttn-next',
        prevEl: '.collection-bttn-prev',
      }
    });
  }

  class ColorSwatchComponent {
    constructor(container) {
      this.container = container;
      this.colorLinks = this.container.querySelectorAll('.color_link');
      this.colorNameSpan = this.container.querySelector('.color_name');
      this.initEvents();
    }

    initEvents() {
      this.colorLinks.forEach(link => {
        link.addEventListener('mouseenter', () => {
          const colorName = link.getAttribute('data-color-name');
          if (this.colorNameSpan) {
            this.colorNameSpan.textContent = ' - ' + colorName;
          }
        });

        link.addEventListener('mouseleave', () => {
          if (this.colorNameSpan) {
            this.colorNameSpan.textContent = '';
          }
        });
      });
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    const swatchComponents = document.querySelectorAll('.color-swatch-component');
    swatchComponents.forEach(component => new ColorSwatchComponent(component));
  });

function getRightSpace() {
  const element = document?.querySelector('.collection-hero');
  const rect = element?.getBoundingClientRect();

  const rightSpace = window.innerWidth - rect?.right;
  document.body.style.setProperty('--right-space', rightSpace + 'px');
  console.log("Right space:", rightSpace + "px");
}

getRightSpace();

window.addEventListener('resize', getRightSpace);


document.addEventListener('DOMContentLoaded', function () {
  const path = window.location.pathname;

  console.log('Right-click protection active for:', path);

  // Allow right-clicks everywhere else
  document.addEventListener('contextmenu', function (e) {
    const target = e.target;

    const isProtectedImage =
      (target.closest('.card-wrapper')) ||
      target.closest('.product__media') ||
      target.closest('.product__image') ||
      target.classList.contains('media') ||
      (target.tagName === 'IMG' && path.startsWith('/products'));

    if (isProtectedImage) {
      e.preventDefault();
    }
  }, { passive: false });
});

document.addEventListener('DOMContentLoaded', function () {
  const path = window.location.pathname;

  // Define labels for specific app pages
  const appBreadcrumbs = {
    '/apps/tracking': 'Track Your Order',
    '/apps/return_prime': 'Returns & Exchanges'
  };

  const breadcrumbLabel = appBreadcrumbs[path];

  if (!breadcrumbLabel) return; // Stop if not an app page we care about

  // Wait for #MainContent (some apps load async)
  const insertBreadcrumb = () => {
    const mainContent = document.querySelector('#MainContent');
    if (!mainContent) {
      // Retry a few times if #MainContent loads late
      return setTimeout(insertBreadcrumb, 300);
    }

    // Create breadcrumb HTML (you can match your existing structure)
    const breadcrumbHTML = `
      <div id="breadcrumb_sec_main">
        <div class="page-width">
          <nav class="sf-breadcrumb w-full">
            <a href="/" class="bread-crumb__item" title="{{ 'general.breadcrumbs.home_link_title' | t }}">
              Poster and Art
            </a> ▸
            <span>${breadcrumbLabel}</span>
          </nav>
        </div>
      </div>
    `;

    // Insert breadcrumb at the top of #MainContent
    mainContent.insertAdjacentHTML('afterbegin', breadcrumbHTML);

    console.log('🧭 Breadcrumb added under #MainContent for:', path);
  };

  insertBreadcrumb();
});

document.addEventListener("DOMContentLoaded", function () {
    const items = document.querySelectorAll(".collection-list .collection-list__item");
    const loadMoreBtn = document.getElementById("collection-button");

    let visibleCount = 30; // Show initially

    // Hide items after the initial count
    items.forEach((item, index) => {
        if (index >= visibleCount) {
            item.style.display = "none";
        }
    });

    // Hide button if all items are already visible
    if (items.length <= visibleCount) {
      loadMoreBtn.style.display = "none";
    }

    // Load more on click
    loadMoreBtn.addEventListener("click", function () {
        let hiddenItems = Array.from(items).filter(item => item.style.display === "none");

        hiddenItems.slice(0, 30).forEach(item => {
            item.style.display = "";
        });

        // Hide button if no more items
        if (hiddenItems.length <= 30) {
            loadMoreBtn.style.display = "none";
        }
    });
});





document.addEventListener("click", (e) => {
  const input = e.target.closest(".measurement");
  if (!input) return;

  const parentSize = input.closest(".product-form__input--pill.Size");
  if (!parentSize) return;

  parentSize.dataset.measurement = input.dataset.measurement;

  parentSize.querySelectorAll(".measurement").forEach(el => {
    el.classList.remove("active");
  });

  input.classList.add("active");

  console.log(parentSize.dataset.measurement);
});